// Central data layer. Loads People / Projects / ProjectTeamMembers / BillableRates
// from SharePoint once after sign-in and shares them via useData().
// Writes (add/edit) go straight to SharePoint, then refresh.

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import * as sp from "../services/sqlApi";
import { ADMIN_EMAILS, FINANCE_EMAILS } from "../services/authConfig";

const DataContext = createContext(null);
export const useData = () => useContext(DataContext);

// Rate conversion lives in one place now (services/rates.js); re-export for
// any callers that still import it from here.
export { monthlyRateForRole, weeklyRateForRole, rateTypeForRole } from "../services/rates";

export function DataProvider({ children }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const [people, setPeople] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [billableRates, setBillableRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The data API verifies our Entra ID TOKEN (audience = this app registration),
  // so hand back idToken rather than a Graph access token.
  // The data API verifies our Entra ID TOKEN (audience = this app registration).
  // MSAL hands back whatever id_token is cached, and those expire after ~1 hour,
  // so refresh when it's close to expiry rather than sending a stale one.
  const getToken = useCallback(async () => {
    const account = instance.getActiveAccount() || accounts[0];
    const req = { scopes: ["User.Read"], account };
    const expSoon = (jwtStr) => {
      try {
        const exp = JSON.parse(atob(jwtStr.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp;
        return !exp || exp * 1000 - Date.now() < 5 * 60 * 1000; // <5 min left
      } catch { return true; }
    };
    try {
      let r = await instance.acquireTokenSilent(req);
      if (!r.idToken || expSoon(r.idToken)) {
        r = await instance.acquireTokenSilent({ ...req, forceRefresh: true });
      }
      return r.idToken;
    } catch (e) {
      const r = await instance.acquireTokenPopup(req);
      return r.idToken;
    }
  }, [instance, accounts]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const [pplR, prjR, tmR, ratesR] = await Promise.allSettled([
        sp.getPeople(token), sp.getProjects(token), sp.getTeamMembers(token), sp.getBillableRates(token),
      ]);
      if (pplR.status === "fulfilled") setPeople(pplR.value);
      if (prjR.status === "fulfilled") setProjects(prjR.value);
      if (tmR.status === "fulfilled") setTeamMembers(tmR.value);
      setBillableRates(ratesR.status === "fulfilled" ? ratesR.value : []); // optional list
      const coreErr = [pplR, prjR, tmR].find(r => r.status === "rejected");
      if (coreErr) setError(coreErr.reason?.message || "Failed to load data from SharePoint.");
    } catch (e) {
      setError(e.message || "Failed to load data from SharePoint.");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (isAuthenticated) load();
  }, [isAuthenticated, load]);

  // ── writes ──
  const addPerson = async (fields) => { const t = await getToken(); await sp.createPerson(t, fields); await load(); };
  const editPerson = async (id, fields) => { const t = await getToken(); await sp.updatePerson(t, id, fields); await load(); };
  const removePerson = async (id) => { const t = await getToken(); await sp.deletePerson(t, id); await load(); };
  const addProject = async (fields) => { const t = await getToken(); await sp.createProject(t, fields); await load(); };
  const editProject = async (id, fields) => { const t = await getToken(); await sp.updateProject(t, id, fields); await load(); };
  const removeProject = async (id) => { const t = await getToken(); await sp.deleteProject(t, id); await load(); };
  const addTeamMember = async (fields) => { const t = await getToken(); await sp.createTeamMember(t, fields); await load(); };
  const editTeamMember = async (id, fields) => { const t = await getToken(); await sp.updateTeamMember(t, id, fields); await load(); };
  const removeTeamMember = async (id) => { const t = await getToken(); await sp.deleteTeamMember(t, id); await load(); };
  const addRate = async (fields) => { const t = await getToken(); await sp.createRate(t, fields); await load(); };
  const editRate = async (id, fields) => { const t = await getToken(); await sp.updateRate(t, id, fields); await load(); };
  const removeRate = async (id) => { const t = await getToken(); await sp.deleteRate(t, id); await load(); };

  // Moving a job's dates is one decision, so it's one write: the project and
  // every affected team member go up together and the data reloads once at the
  // end rather than once per person.
  const moveJobAndCrew = async (projectId, fields, memberUpdates = []) => {
    const t = await getToken();
    if (projectId) await sp.updateProject(t, projectId, fields);
    for (const u of memberUpdates) await sp.updateTeamMember(t, u.Id, { StartDate: u.StartDate, EndDate: u.EndDate });
    await load();
  };

  // ── deleted projects (recycle bin) ──
  const getDeletedProjects = async () => { const t = await getToken(); return sp.getDeletedProjects(t); };
  const restoreProject = async (id) => { const t = await getToken(); await sp.restoreProject(t, id); await load(); };

  // ── budget snapshots (Procore) ──
  const getBudgetSnapshots = async (jobNumber) => { const t = await getToken(); return sp.getBudgetSnapshots(t, jobNumber); };
  const saveBudgetSnapshotRows = async (rows) => {
    const t = await getToken();
    for (const f of rows) await sp.createBudgetSnapshotRow(t, f);
  };

  // ── runoff matrix (Financials tab) — loaded on demand, not at boot ──
  const getRunoffEntries = async () => { const t = await getToken(); return sp.getRunoffEntries(t); };
  const addRunoffEntry = async (fields) => { const t = await getToken(); return sp.createRunoffEntry(t, fields); };
  const editRunoffEntry = async (id, fields) => { const t = await getToken(); return sp.updateRunoffEntry(t, id, fields); };
  const removeRunoffEntry = async (id) => { const t = await getToken(); return sp.deleteRunoffEntry(t, id); };

  // ── current user / admin / finance ──
  const account = instance.getActiveAccount() || accounts[0] || null;
  const claims = account?.idTokenClaims || {};
  // A person's sign-in identity can surface under different keys — the UPN
  // (account.username), the primary SMTP (email claim), an alias, etc. Match
  // roles against ALL of them so an admin/finance address that's an alias rather
  // than the exact UPN (common in M365) still grants access.
  const myEmails = [account?.username, claims.preferred_username, claims.email, ...(Array.isArray(claims.emails) ? claims.emails : [])]
    .filter(Boolean).map(e => String(e).toLowerCase());
  const myEmail = myEmails[0] || "";
  const inList = (list) => list.some(e => myEmails.includes(String(e).toLowerCase()));
  const myRecord = people.find(p => myEmails.includes((p.Email || "").toLowerCase()));
  const isAdmin = inList(ADMIN_EMAILS) || myRecord?.IsAdmin === true;
  const isFinance = isAdmin || inList(FINANCE_EMAILS) || myRecord?.IsFinance === true;

  const value = {
    people, projects, teamMembers, billableRates,
    loading, error, reload: load, getToken,
    myEmail, isAdmin, isFinance, bootstrapAdmins: ADMIN_EMAILS,
    addPerson, editPerson, removePerson, addProject, editProject, removeProject, moveJobAndCrew,
    addTeamMember, editTeamMember, removeTeamMember, addRate, editRate, removeRate,
    getBudgetSnapshots, saveBudgetSnapshotRows, getDeletedProjects, restoreProject,
    getRunoffEntries, addRunoffEntry, editRunoffEntry, removeRunoffEntry,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}
